/**
 * GraphStatusBanner.tsx
 * ──────────────────────
 * Bannière non-bloquante affichée au-dessus des tabs de la fiche.
 * Reflète l'état du Legal Graph retourné par useGraphCheck.
 */

import { useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import type { GraphStatus, GraphCheckResult } from '@/hooks/useGraphCheck';
import { GraphDebugPanel } from './GraphDebugPanel';

interface Props {
  status:         GraphStatus;
  report:         GraphCheckResult | null;
  isAdmin:        boolean;
  triggerRebuild: () => void;
  rebuildError:   string | null;
}

export function GraphStatusBanner({
  status,
  report,
  isAdmin,
  triggerRebuild,
  rebuildError,
}: Props) {
  const { t } = useTranslation();
  // Rien à afficher si le graph est OK ou pas encore vérifié
  if (status === 'idle' || status === 'checking') return null;

  // ── Graph OK + admin → barre discrète avec bouton Reconstruire + Analyser ─
  if (status === 'ok') {
    if (!isAdmin) return null;
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4 px-4 py-3 rounded-xl bg-muted/40 border border-border">
          <div className="flex items-center gap-2.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
            <p className="text-xs text-muted-foreground">
              {t('graph.status.built_on')}{' '}
              <span className="font-mono">
                {report?.graph_built_at
                  ? new Date(report.graph_built_at).toLocaleDateString(getDateLocale())
                  : '—'}
              </span>
              {report && report.procedure_graph_size > 0 && (
                <span className="ml-1.5 opacity-60">
                  · {t('graph.status.links', { count: report.procedure_graph_size })}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={triggerRebuild}
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('graph.status.rebuild')}
          </button>
        </div>
        {report && <GraphDebugPanel report={report as any} onRebuild={triggerRebuild} />}
      </div>
    );
  }

  // ── Graph absent ou anomalies → bannière "mise à jour" ───────────────────
  if (status === 'updating') {
    return (
      <div className="mb-6 flex items-center justify-between gap-4 px-4 py-3 rounded-xl bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-950/30 dark:to-blue-950/30 border border-indigo-200/60 dark:border-indigo-800/40">
        <div className="flex items-center gap-3 min-w-0">
          <span className="relative flex h-2.5 w-2.5 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-indigo-500" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-indigo-800 dark:text-indigo-300">
              {t('graph.status.updating_title')}
            </p>
            <p className="text-xs text-indigo-600/70 dark:text-indigo-400/70 mt-0.5">
              {!report?.graph_built_at
                ? t('graph.status.initial_build')
                : t('graph.status.optimizations', { count: report.anomalies.length })
              }
            </p>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={triggerRebuild}
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('graph.status.rebuild')}
          </button>
        )}
      </div>
    );
  }

  // ── Rebuild en cours ──────────────────────────────────────────────────────
  if (status === 'rebuilding') {
    return (
      <div className="mb-6 flex items-center gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-950/30 dark:to-blue-950/30 border border-indigo-200/60 dark:border-indigo-800/40">
        <svg className="w-4 h-4 text-indigo-600 animate-spin shrink-0" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <div>
          <p className="text-sm font-semibold text-indigo-800 dark:text-indigo-300">
            {t('graph.status.rebuilding_title')}
          </p>
          <p className="text-xs text-indigo-600/70 dark:text-indigo-400/70 mt-0.5">
            {t('graph.status.rebuilding_desc')}
          </p>
        </div>
      </div>
    );
  }

  // ── Rebuild terminé ───────────────────────────────────────────────────────
  if (status === 'done') {
    return (
      <div className="mb-6 flex items-center gap-3 px-4 py-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-800/40">
        <svg className="w-4 h-4 text-emerald-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">
          {t('graph.status.done')}
        </p>
      </div>
    );
  }

  // ── Erreur rebuild ────────────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <div className="mb-6 flex items-center justify-between gap-4 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200/60 dark:border-red-800/40">
        <div className="flex items-center gap-3 min-w-0">
          <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-red-700 dark:text-red-300">
            {rebuildError || t('graph.status.error')}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={triggerRebuild}
            className="shrink-0 text-xs font-medium px-3 py-1.5 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
          >
            {t('common.retry')}
          </button>
        )}
      </div>
    );
  }

  return null;
}